import type { Currency, DueEntry, FxRates, SubPayment, Subscription } from "../../shared/money";

/** Amounts travel as minor units (kopiykas/cents) everywhere. */
export const toMajor = (minor: number): number => minor / 100;
export const toMinor = (major: number): number => Math.round(major * 100);

const SYMBOL: Record<Currency, string> = { UAH: "₴", USD: "$", EUR: "€" };

export function formatMoney(minor: number, currency: Currency): string {
  const value = toMajor(minor);
  const text = new Intl.NumberFormat("uk-UA", {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value);
  return currency === "UAH" ? `${text} ₴` : `${SYMBOL[currency]}${text}`;
}

/** Compact UAH for summary tiles: 12 345 ₴. */
export function formatUah(minor: number): string {
  return formatMoney(minor, "UAH");
}

export function dueLabel(daysLeft: number): string {
  if (daysLeft < 0) return `${-daysLeft} d overdue`;
  if (daysLeft === 0) return "today";
  if (daysLeft === 1) return "tomorrow";
  return `in ${daysLeft} d`;
}

export function dueColor(daysLeft: number): "red" | "orange" | "yellow" | "gray" {
  if (daysLeft < 0) return "red";
  if (daysLeft <= 1) return "orange";
  if (daysLeft <= 7) return "yellow";
  return "gray";
}

export function formatDay(day: string): string {
  const [y, m, d] = day.split("-").map(Number);
  if (!y || !m || !d) return day;
  return new Date(y, m - 1, d).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

/** Rates the user cares about: what the card charges vs the official rate. */
export function fxLine(fx: FxRates): string {
  return `USD ${fx.mono.USD.toFixed(2)} mono · ${fx.nbu.USD.toFixed(2)} NBU`;
}

export interface SubProgress {
  paid: number;
  total: number;
  paidUah: number;
  leftUah: number;
  /** 0..1 */
  ratio: number;
}

export function installmentProgress(sub: Subscription, payments: SubPayment[]): SubProgress {
  const mine = payments.filter((p) => p.subId === sub.id);
  const total = sub.parts ?? 0;
  const paid = Math.min(mine.length, total);
  const paidUah = mine.reduce((sum, p) => sum + p.amountUah, 0);
  return {
    paid,
    total,
    paidUah,
    leftUah: Math.max(0, total - paid) * sub.amount,
    ratio: total > 0 ? paid / total : 0,
  };
}

export function nextDueFor(subId: string, due: DueEntry[]): DueEntry | undefined {
  return due.find((d) => d.subId === subId);
}

/** Monthly-normalized cost of a recurring sub, in its own currency. */
export function perMonth(sub: Subscription): number {
  return sub.cycle === "yearly" ? Math.round(sub.amount / 12) : sub.amount;
}
